import { useCallback, useEffect, useRef, useState } from "react";
import { MapPin } from "lucide-react";
import { useTranslation } from "react-i18next";
import { AddressAutocompleteInput } from "./AddressAutocompleteInput";
import { ExpandableMapFrame } from "./ExpandableMapFrame";
import { LocationPickerMap } from "./LocationPickerMap";
import { Alert } from "@/components/ui";
import { geocodingService } from "@/services/geocodingService";
import type { MapCamera } from "@/hooks/useEmbeddedMap";
import { getMapboxPublicToken } from "@/lib/mapboxConfig";
import { cn } from "@/lib/utils";
import type { GeocodedLocationValue, GeoCoordinates } from "@/lib/geocodedLocation";
import type {
  AddressSuggestion,
  AddressValidationState,
  AddressValidationStatus,
} from "@/types";

interface EditableGeocodedLocationLabels {
  search: string;
  searchPlaceholder: string;
}

interface EditableGeocodedLocationProps {
  value: GeocodedLocationValue;
  onChange: (value: GeocodedLocationValue) => void;
  syncKey?: string;
  labels: EditableGeocodedLocationLabels;
  error?: string;
  onValidationChange?: (state: AddressValidationState) => void;
  /** Prefer stacked layout inside narrow modals so the map stays usable. */
  layout?: "split" | "stack";
}

const GEOCODE_DEBOUNCE_MS = 350;
const MIN_GEOCODE_LENGTH = 3;

export function EditableGeocodedLocation({
  value,
  onChange,
  syncKey,
  labels,
  error,
  onValidationChange,
  layout = "split",
}: EditableGeocodedLocationProps) {
  const { t } = useTranslation("common");
  const cameraRef = useRef<MapCamera | null>(null);
  const onValidationChangeRef = useRef(onValidationChange);
  const onChangeRef = useRef(onChange);
  const valueRef = useRef(value);
  onValidationChangeRef.current = onValidationChange;
  onChangeRef.current = onChange;
  valueRef.current = value;

  const geocodeDebounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const geocodeRequestIdRef = useRef(0);
  const lastEmittedValidationRef = useRef<AddressValidationStatus | null>(null);

  const [searchDisplay, setSearchDisplay] = useState(value.address);
  const valueAddress = value.address;
  const valueLat = value.coordinates?.lat;
  const valueLng = value.coordinates?.lng;

  const mapboxToken = getMapboxPublicToken();

  const emitValidation = useCallback((next: GeocodedLocationValue) => {
    if (!onValidationChangeRef.current) return;

    let status: AddressValidationStatus;
    if (next.coordinates) {
      status = "valid";
    } else if (!next.address.trim()) {
      status = "idle";
    } else {
      status = "incomplete";
    }

    if (lastEmittedValidationRef.current === status) return;
    lastEmittedValidationRef.current = status;
    onValidationChangeRef.current({ status });
  }, []);

  useEffect(() => {
    setSearchDisplay(valueRef.current.address);
    lastEmittedValidationRef.current = null;
  }, [syncKey]);

  useEffect(() => {
    const coordinates =
      typeof valueLat === "number" && typeof valueLng === "number"
        ? { lat: valueLat, lng: valueLng }
        : null;
    emitValidation({ address: valueAddress, coordinates });
  }, [syncKey, valueAddress, valueLat, valueLng, emitValidation]);

  useEffect(() => {
    return () => {
      if (geocodeDebounceRef.current) {
        clearTimeout(geocodeDebounceRef.current);
      }
    };
  }, []);

  const applyMapCoordinates = useCallback(
    (coordinates: GeoCoordinates) => {
      const next: GeocodedLocationValue = {
        address: valueRef.current.address,
        coordinates,
      };
      onChangeRef.current(next);
      emitValidation(next);
    },
    [emitValidation],
  );

  const scheduleGeocode = useCallback(
    (address: string) => {
      if (geocodeDebounceRef.current) {
        clearTimeout(geocodeDebounceRef.current);
      }

      const trimmed = address.trim();
      if (trimmed.length < MIN_GEOCODE_LENGTH) {
        return;
      }

      geocodeDebounceRef.current = setTimeout(() => {
        const requestId = ++geocodeRequestIdRef.current;
        void geocodingService
          .resolveAddress(trimmed)
          .then((result) => {
            if (requestId !== geocodeRequestIdRef.current) return;
            if (!result.coordinates) return;
            if (valueRef.current.address.trim() !== trimmed) return;
            const next: GeocodedLocationValue = {
              address: valueRef.current.address,
              coordinates: result.coordinates,
            };
            onChangeRef.current(next);
            emitValidation(next);
          })
          .catch(() => {
            // Keep existing coordinates when geocoding fails.
          });
      }, GEOCODE_DEBOUNCE_MS);
    },
    [emitValidation],
  );

  const handleSearchChange = (nextAddress: string) => {
    setSearchDisplay(nextAddress);
    onChange({
      address: nextAddress,
      coordinates: valueRef.current.coordinates,
    });
    scheduleGeocode(nextAddress);
  };

  const handleSelect = async (
    suggestion: AddressSuggestion,
    sessionToken: string,
  ) => {
    if (geocodeDebounceRef.current) {
      clearTimeout(geocodeDebounceRef.current);
    }
    geocodeRequestIdRef.current += 1;

    let resolved: typeof suggestion;
    try {
      resolved = await geocodingService.retrieve(suggestion.id, sessionToken);
    } catch {
      resolved = suggestion;
    }

    const label = resolved.label.trim();
    setSearchDisplay(label);
    const coordinates = resolved.coordinates ?? null;
    onChange({
      address: label,
      coordinates,
    });
    emitValidation({ address: label, coordinates });
  };

  const showMap = Boolean(mapboxToken);
  const stacked = layout === "stack";

  return (
    <div className="space-y-3">
      <div
        className={cn(
          "grid grid-cols-1 gap-4",
          !stacked && "lg:grid-cols-2 lg:items-stretch",
        )}
      >
        <div className="flex min-w-0 flex-col gap-3">
          <AddressAutocompleteInput
            label={labels.search}
            placeholder={labels.searchPlaceholder}
            value={searchDisplay}
            onChange={handleSearchChange}
            onSelect={handleSelect}
            error={error}
          />
          {value.coordinates ? (
            <div className="flex items-center gap-3 rounded-lg border border-surface-200 bg-surface-50 px-3.5 py-2.5">
              <span
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600 ring-1 ring-inset ring-primary-600/15"
                aria-hidden
              >
                <MapPin className="h-4 w-4" />
              </span>
              <div className="min-w-0 flex-1">
                {value.address.trim() ? (
                  <p className="text-sm font-medium text-surface-900">
                    {value.address}
                  </p>
                ) : null}
                <p className="mt-0.5 font-mono text-xs tabular-nums text-surface-500">
                  {value.coordinates.lat.toFixed(5)}, {value.coordinates.lng.toFixed(5)}
                </p>
              </div>
            </div>
          ) : null}
          {showMap && !stacked ? (
            <p className="mt-auto hidden items-center gap-1.5 text-xs text-surface-500 lg:flex">
              <MapPin className="h-3.5 w-3.5" />
              {t("editableLocation.mapHint")}
            </p>
          ) : null}
        </div>

        {showMap ? (
          <ExpandableMapFrame
            className={cn(
              "rounded-lg border border-surface-200",
              stacked ? "min-h-[280px]" : "h-full lg:min-h-[280px]",
            )}
            mapClassName={
              stacked
                ? "h-64 sm:h-72"
                : "h-56 sm:h-64 lg:h-full lg:min-h-[280px]"
            }
          >
            {(expanded) => (
              <LocationPickerMap
                coordinates={value.coordinates}
                onPick={applyMapCoordinates}
                cameraRef={cameraRef}
                cooperativeGestures={!expanded}
                ariaLabel={t("editableLocation.mapAriaLabel")}
              />
            )}
          </ExpandableMapFrame>
        ) : null}
      </div>

      {!value.coordinates && value.address.trim() ? (
        <Alert variant="warning" className="px-3 py-2">
          {t("editableLocation.noCoordinatesWarning")}
        </Alert>
      ) : null}

      {showMap ? (
        <p
          className={cn(
            "flex items-center gap-1.5 text-xs text-surface-500",
            !stacked && "lg:hidden",
          )}
        >
          <MapPin className="h-3.5 w-3.5" />
          {t("editableLocation.mapHint")}
        </p>
      ) : null}

      {!mapboxToken && value.coordinates ? (
        <p className="text-xs text-surface-500">{t("editableLocation.mapUnavailable")}</p>
      ) : null}
    </div>
  );
}
